'use client';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SectionTitle } from '@benzinga/core-ui';
import type { CompanyEventDetails } from '@benzinga/calendar-manager';

import type { BaseQuotePageProps } from '../../../../../src/utils/quote/types';
import { quoteHeadingFromMetaTitle } from '../../../../../src/utils/quote/helpers';

import CompanyEventsTable from '../../../../../src/components/Quote/CompanyEventsTable';
import DrilldownFooter from '../../../../../src/components/Quote/DrilldownFooter';
import { EventsFAQ } from '../../../../../src/components/Quote/FAQ/EventsFAQ';
import { QuoteFooter } from '../../../../../src/components/Quote/QuoteFooter';

interface EventsContentProps extends BaseQuotePageProps {
  companyEvents: CompanyEventDetails[] | null;
}

const EventsContent: React.FC<EventsContentProps> = ({ companyEvents, metaProps, profile, symbol }) => {
  const { t } = useTranslation(['quote', 'common']);
  const companyName = profile?.richQuoteData?.companyStandardName ?? profile?.richQuoteData?.name ?? symbol;

  return (
    <div className="main-div flex flex-col gap-y-8 px-4">
      <div className="flex flex-col lg:flex-row">
        <div className="flex w-full flex-col gap-y-8">
          <div className="flex w-full flex-col gap-2">
            <SectionTitle level={2} uppercase={false}>
              {quoteHeadingFromMetaTitle(metaProps.title)}
            </SectionTitle>
            <p className="section-description mb-0">
              {t('Quote.Paragraphs.events', {
                companyName,
              })}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <CompanyEventsTable events={companyEvents ?? []} />
            <p className="text-sm text-gray-600">
              {t('Quote.Events.footer-browse-market')}{' '}
              <a className="font-semibold text-blue-600 no-underline hover:text-blue-800" href="/calendars/events">
                {t('Quote.Events.footer-browse-market-link')}
              </a>
              .
            </p>
          </div>
        </div>
      </div>
      <DrilldownFooter
        isETF={profile.isETF}
        leftSection={
          <>
            <SectionTitle level={3} size="xl" uppercase={false}>
              {t('Quote.FAQ.faq')}
            </SectionTitle>
            {Array.isArray(companyEvents) && profile?.richQuoteData ? (
              <EventsFAQ companyEvents={companyEvents} quote={profile.richQuoteData} />
            ) : null}
          </>
        }
        peers={profile.peers}
        peerSymbols={profile.peerSymbols}
        symbol={symbol}
        type={profile.schedule?.type ?? null}
      />
      <QuoteFooter activeTab="events" />
    </div>
  );
};

export default EventsContent;
